import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LoginForm } from '../../components/auth';
import { BarChart3, TrendingUp, Shield, Zap } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  return <AuthLayout><LoginForm onSuccess={() => navigate('/dashboard')} /></AuthLayout>;
};

/* ── Shared auth page shell ── */
export const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #06091a 0%, #0d1628 50%, #060b14 100%)',
      display: 'flex',
      alignItems: 'stretch',
    }}
  >
    {/* Left panel — branding (hidden on mobile) */}
    <div
      className="hidden lg:flex"
      style={{
        display: 'none',
        width: '44%',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '3rem',
        borderRight: '1px solid rgba(255,255,255,0.05)',
        background: 'rgba(10,14,26,0.5)',
        backdropFilter: 'blur(8px)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient glow */}
      <div aria-hidden="true" style={{ position: 'absolute', top: '10%', left: '20%', width: 360, height: 360, background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
      <div aria-hidden="true" style={{ position: 'absolute', bottom: '15%', right: '10%', width: 280, height: 280, background: 'radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

      {/* Logo */}
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', textDecoration: 'none', position: 'relative', zIndex: 1 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 16px rgba(59,130,246,0.3)' }}>
          <BarChart3 size={20} color="#fff" strokeWidth={2.5} />
        </div>
        <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em' }}>
          Nitrex<span style={{ color: '#60a5fa' }}>trader</span>
        </span>
      </Link>

      {/* Headline */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.04em', lineHeight: 1.2, marginBottom: '1rem' }}>
          AI-powered stock<br />
          <span style={{ background: 'linear-gradient(120deg, #60a5fa, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            analysis at your fingertips
          </span>
        </h1>
        <p style={{ fontSize: '0.9375rem', color: '#64748b', lineHeight: 1.7, marginBottom: '2rem' }}>
          8 layers of AI analysis, real-time data, and portfolio management — all in one platform.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          {[
            { Icon: TrendingUp, text: 'Live BUY / HOLD / SELL signals', color: '#10b981' },
            { Icon: Zap,        text: 'Real-time quotes & market news',  color: '#3b82f6' },
            { Icon: Shield,     text: 'Powered by AWS Bedrock AgentCore', color: '#8b5cf6' },
          ].map(({ Icon, text, color }, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={15} color={color} strokeWidth={2} />
              </div>
              <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: 500 }}>{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer note */}
      <p style={{ fontSize: '0.72rem', color: '#1e2d45', position: 'relative', zIndex: 1 }}>
        For informational purposes only. Not financial advice.
      </p>
    </div>

    {/* Right panel — form */}
    <div style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1.5rem',
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Mobile logo */}
        <div className="lg:hidden" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BarChart3 size={17} color="#fff" strokeWidth={2.5} />
            </div>
            <span style={{ fontSize: '1.125rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em' }}>
              Nitrex<span style={{ color: '#60a5fa' }}>trader</span>
            </span>
          </Link>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(17,24,39,0.8)',
          border: '1px solid #1e2d45',
          borderRadius: 18,
          padding: '2.25rem',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }}>
          {children}
        </div>
      </div>
    </div>
  </div>
);
